/**
 * useTheme Composable
 *
 * Manages light/dark theme with:
 * - System preference detection
 * - Local storage persistence
 * - Reactive theme state
 */

import { ref, watchEffect, computed } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'tuscourses-theme'

// Singleton state (shared across all component instances)
const theme = ref<ThemeMode>(getStoredTheme())
const isDark = ref(false)

/**
 * Get stored theme from localStorage
 * Default: 'dark' (TuS Wannsee brand preference)
 */
function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'dark'

  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }
  return 'dark'
}

/**
 * Check if system prefers dark mode
 */
function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Apply theme to document
 */
function applyTheme(dark: boolean): void {
  if (typeof document === 'undefined') return

  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }

  // Update meta theme-color for mobile browsers
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', dark ? '#1f2937' : '#ffffff')
  }
}

/**
 * useTheme Composable
 */
export function useTheme() {
  // Watch theme changes and apply
  watchEffect(() => {
    const prefersDark = systemPrefersDark()

    isDark.value = theme.value === 'dark' || (theme.value === 'system' && prefersDark)

    applyTheme(isDark.value)

    // Persist to localStorage
    localStorage.setItem(STORAGE_KEY, theme.value)
  })

  // Listen for system theme changes
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      if (theme.value === 'system') {
        isDark.value = e.matches
        applyTheme(isDark.value)
      }
    })
  }

  /**
   * Set theme mode
   */
  function setTheme(mode: ThemeMode): void {
    theme.value = mode
  }

  /**
   * Toggle between light and dark
   */
  function toggleTheme(): void {
    if (theme.value === 'system') {
      // If system, switch to opposite of current
      theme.value = isDark.value ? 'light' : 'dark'
    } else {
      theme.value = theme.value === 'dark' ? 'light' : 'dark'
    }
  }

  /**
   * Cycle through themes: light -> dark -> system -> light
   */
  function cycleTheme(): void {
    const order: ThemeMode[] = ['light', 'dark', 'system']
    const currentIndex = order.indexOf(theme.value)
    const nextIndex = (currentIndex + 1) % order.length
    theme.value = order[nextIndex]!
  }

  /**
   * Icon for current theme
   */
  const themeIcon = computed(() => {
    switch (theme.value) {
      case 'light':
        return 'pi pi-sun'
      case 'dark':
        return 'pi pi-moon'
      case 'system':
      default:
        return 'pi pi-desktop'
    }
  })

  /**
   * Label for current theme
   */
  const themeLabel = computed(() => {
    switch (theme.value) {
      case 'light':
        return 'Hell'
      case 'dark':
        return 'Dunkel'
      case 'system':
      default:
        return 'System'
    }
  })

  return {
    // State
    theme,
    isDark,

    // Computed
    themeIcon,
    themeLabel,

    // Actions
    setTheme,
    toggleTheme,
    cycleTheme,
  }
}
